/**
 * This type contains helper methods to handle parsing.
 */
export class ParsingHelper {

	/** Root font size in px, used to convert between rem and px. */
	public static readonly ROOT_FONT_SIZE = 16;

	/**
	 * Parse size to px
	 * @param size
	 * @param rootFontSize
	 * @param referencePx
	 */
	public static parseSizeToPx(size: string, rootFontSize = ParsingHelper.ROOT_FONT_SIZE, referencePx = 1000): number|null {
		if (!size) return null;
		const s = String(size).trim();
		if (s.endsWith('px')) return parseFloat(s);
		if (s.endsWith('rem')) return parseFloat(s) * rootFontSize;
		if (s.endsWith('%')) return (parseFloat(s) / 100) * referencePx;
		const num = parseFloat(s);
		return Number.isFinite(num) ? num : null;
	}

	/**
	 * Convert a px value (number or string such as "120" or "120px") to its numeric rem equivalent.
	 * Unit-less input is treated as px. Inverse of {@link remToPx}.
	 * @param px
	 */
	public static pxToRem(px: string | number): number {
		return (this.parseSizeToPx(String(px)) ?? 0) / this.ROOT_FONT_SIZE;
	}

	/**
	 * Convert a rem magnitude (number or string such as "7.5" or "7.5rem") to its numeric px equivalent.
	 * Unit-less input is treated as rem (the format column widths are persisted in). Inverse of {@link pxToRem}.
	 * @param rem
	 */
	public static remToPx(rem: string | number): number {
		return this.parseSizeToPx(`${parseFloat(String(rem))}rem`) ?? 0;
	}

	/**
	 * Normalizes a number string with mixed thousands/decimal separators into a dot-decimal value
	 * ("1.234,56" and "1,234.56" both become "1234.56"). Returns "" when blank and the original
	 * value when it cannot be parsed.
	 */
	public static normalizeDecimal(value: string): string {
		const trimmed = value.replace(/\s/g, "");
		if (!trimmed.length) {
			return "";
		}

		const lastComma = trimmed.lastIndexOf(",");
		const lastDot = trimmed.lastIndexOf(".");
		let sanitized = trimmed;
		if (lastComma !== -1 && lastDot !== -1) {
			sanitized = lastComma > lastDot ? trimmed.replaceAll(".", "").replace(",", ".") : trimmed.replaceAll(",", "");
		} else if (lastComma !== -1) {
			sanitized = trimmed.replace(",", ".");
		} else if (trimmed.split(".").length > 2) {
			const segments = trimmed.split(".");
			const decimals = segments.pop();
			sanitized = `${segments.join("")}.${decimals}`;
		}

		return Number.isNaN(Number.parseFloat(sanitized)) ? value : sanitized;
	}

	/**
	 * Formats an amount string for read-only display: strips noise, normalizes the separators and
	 * returns a localized value with at least two fraction digits. Blank input becomes "0,00".
	 */
	public static formatAmount(value: string, locale: string): string {
		const cleaned = String(value).replace(/[^0-9.,-]/g, "");
		if (!cleaned.length) {
			return "0,00";
		}

		return Number.parseFloat(this.normalizeDecimal(cleaned)).toLocaleString(locale, {minimumFractionDigits: 2});
	}

}
